import {
    BondQuantityType,
    ModifyOrderFormValues,
    OrderPreview,
    OrderRef,
    PriceType,
} from '@orbis-systems/orbis-api-middleware';
import { SuccessClientResponse } from '@orbis-systems/orbis-api-clients';
import { queryOptions } from '@tanstack/react-query';

import {
    InventoryEquityPreviewPayload,
    AccountEquityPreviewPayload,
    InventoryEquityPlacePayload,
    ModelEquityPreviewPayload,
    AccountEquityPlacePayload,
    AlgoEquityPreviewPayload,
    ModelEquityPlacePayload,
    AlgoEquityPlacePayload,
} from './types';
import { orbis, orbisMiddleware } from '../api';

export interface TradingModifyOrderParams {
    values: ModifyOrderFormValues;
    accountId: number;
    accountNumber: string;
    marketCode: string;
}

export interface TradingCancelOrderParams {
    orderRef: string;
    accountId: number;
    marketCode: string;
}

export interface TradingGetOrderDetailsParams {
    accountId: number;
    orderRef: string;
    market: string;
    loadQuotes: boolean;
    bondPriceType: PriceType;
    bondQuantityType: BondQuantityType;
}

export const tradingQueries = {
    all: () => queryOptions({ queryKey: ['trading'] }),
    orderStatus: (params: TradingGetOrderDetailsParams) =>
        queryOptions({
            queryKey: [...tradingQueries.all().queryKey, 'orderStatus', params],
            queryFn: () =>
                orbis.orders.status(params.orderRef, {
                    accountId: params.accountId,
                    loadQuotes: params.loadQuotes,
                    bondPriceType: params.bondPriceType,
                    bondQuantityType: params.bondQuantityType,
                    market: params.market,
                }),
        }),
} as const;

export const tradingMutations = {
    previewAccountEquityOrder: (params: AccountEquityPreviewPayload): Promise<SuccessClientResponse<OrderPreview>> =>
        orbis.orders.v2.equities.preview(params),
    previewModelEquityOrder: (params: ModelEquityPreviewPayload): Promise<SuccessClientResponse<OrderPreview>> =>
        orbis.orders.v2.equities.advisoryPreview(params),
    previewAlgoEquityOrder: (params: AlgoEquityPreviewPayload): Promise<SuccessClientResponse<OrderPreview>> =>
        orbis.orders.v2.equities.algoPreview(params),
    previewInventoryEquityOrder: (
        params: InventoryEquityPreviewPayload,
    ): Promise<SuccessClientResponse<OrderPreview>> => orbis.orders.v2.equities.advisoryPreview(params),
    placeInventoryEquityOrder: (params: InventoryEquityPlacePayload): Promise<SuccessClientResponse<OrderRef>> =>
        orbis.orders.v2.equities.advisoryPlacement(params),
    placeAccountEquityOrder: (params: AccountEquityPlacePayload): Promise<SuccessClientResponse<OrderRef>> =>
        orbis.orders.v2.equities.placement(params),
    placeModelEquityOrder: (params: ModelEquityPlacePayload): Promise<SuccessClientResponse<OrderRef>> =>
        orbis.orders.v2.equities.advisoryPlacement(params),
    placeAlgoEquityOrder: (params: AlgoEquityPlacePayload): Promise<SuccessClientResponse<OrderRef>> =>
        orbis.orders.v2.equities.algoPlacement(params),
    modifyOrder: (params: TradingModifyOrderParams): Promise<string> => orbisMiddleware.trading.modifyOrder(params),
    cancelOrder: (params: TradingCancelOrderParams): Promise<string> =>
        orbisMiddleware.trading.cancelOrder(params.orderRef, params.accountId, params.marketCode),
} as const;
